import { useState } from 'react';

interface Plan {
  name: string;
  price: number;
  interval: string;
  features: string[];
  popular?: boolean;
  icon: string;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    name: 'Free Listing',
    price: 0,
    interval: 'forever',
    icon: 'ri-store-2-line',
    features: [
      'Business name, address, phone',
      'Operating hours display',
      'Category placement',
      'Basic contact information',
    ],
  },
  {
    name: 'Enhanced Listing',
    price: 49,
    interval: 'month',
    icon: 'ri-image-line',
    features: [
      'Everything in Free',
      'Up to 10 business photos',
      'Website link & social media links',
      'Post deals & coupons',
      'Respond to customer reviews',
      'Enhanced business description',
    ],
  },
  {
    name: 'Featured Listing',
    price: 99,
    interval: 'month',
    icon: 'ri-star-line',
    popular: true,
    badge: 'FEATURED',
    features: [
      'Everything in Enhanced',
      'Homepage spotlight placement',
      '"Featured" badge on listing',
      'Priority in search results',
      'Analytics dashboard with insights',
      'Weekly performance reports',
    ],
  },
  {
    name: 'Premium Listing',
    price: 199,
    interval: 'month',
    icon: 'ri-vip-crown-line',
    badge: 'PREMIUM',
    features: [
      'Everything in Featured',
      'Featured on neighborhood pages',
      'Mentioned in blog articles',
      'Included in email newsletters',
      'Dedicated business profile page',
      'Priority customer support',
      'Advanced SEO optimization',
    ],
  },
];

interface PlanStepProps {
  onSelectPlan: (planName: string) => void;
  isProcessing: boolean;
  onBack: () => void;
}

export default function PlanStep({ onSelectPlan, isProcessing, onBack }: PlanStepProps) {
  const [selectedPlan, setSelectedPlan] = useState<string>('Featured Listing');

  const handleContinue = () => {
    if (selectedPlan) {
      onSelectPlan(selectedPlan);
    }
  };

  return (
    <section className="pb-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-bank-card-line text-teal-600"></i>
            </div>
            <span className="text-sm font-semibold text-teal-700">Step 3 of 3</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Choose Your Listing Plan</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Select the plan that best fits your business needs. Start free or upgrade for maximum visibility.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              onClick={() => setSelectedPlan(plan.name)}
              className={`relative bg-white rounded-2xl p-6 border-2 transition-all cursor-pointer hover:shadow-lg ${
                selectedPlan === plan.name
                  ? 'border-teal-500 shadow-lg'
                  : 'border-gray-200 hover:border-teal-300'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-amber-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    selectedPlan === plan.name ? 'bg-teal-100' : 'bg-gray-100'
                  }`}
                >
                  <i
                    className={`${plan.icon} text-2xl ${
                      selectedPlan === plan.name ? 'text-teal-600' : 'text-gray-500'
                    }`}
                  ></i>
                </div>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.name ? 'border-teal-500 bg-teal-500' : 'border-gray-300'
                  }`}
                >
                  {selectedPlan === plan.name && (
                    <i className="ri-check-line text-white text-sm"></i>
                  )}
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
              <div className="mb-4">
                {plan.price === 0 ? (
                  <span className="text-4xl font-bold text-gray-900">Free</span>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-500 ml-1">/{plan.interval}</span>
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-teal-600"></i>
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-teal-50 to-amber-50 rounded-2xl p-6 border border-teal-100 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">14-Day Money-Back Guarantee</h4>
                <p className="text-sm text-gray-600">
                  Not satisfied with your paid listing? Get a full refund within the first 14 days, no questions asked.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={onBack}
              className="px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedPlan || isProcessing}
              className="flex-1 py-4 bg-teal-600 text-white rounded-xl font-bold text-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Processing...
                </>
              ) : selectedPlan === 'Free Listing' ? (
                <>
                  Submit Free Listing
                  <i className="ri-arrow-right-line"></i>
                </>
              ) : (
                <>
                  Proceed to Payment
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4">
            {selectedPlan === 'Free Listing' 
              ? 'No credit card required • Upgrade anytime'
              : 'Secure payment powered by Stripe • Cancel anytime'}
          </p>
        </div>
      </div>
    </section>
  );
}